import React, { useState, useRef, useEffect } from "react";
import { X, Bug, CheckCircle, AlertTriangle, XCircle, ChevronDown, ChevronRight } from "lucide-react";
import { debugAPI } from "../../services/api/debug";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  ok:    { pill: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  warn:  { pill: "bg-amber-50 text-amber-700 border-amber-200",       dot: "bg-amber-500"   },
  error: { pill: "bg-red-50 text-red-700 border-red-200",             dot: "bg-red-500"     },
};

const STATUS_ICONS = {
  ok:    <CheckCircle className="h-3.5 w-3.5" />,
  warn:  <AlertTriangle className="h-3.5 w-3.5" />,
  error: <XCircle className="h-3.5 w-3.5" />,
};

const SUBSYSTEM_LABELS = {
  recording:      "Recording",
  audio_analysis: "Audio",
  llm_metrics:    "LLM Metrics",
  jobs:           "Jobs",
  agent_config:   "Agent Config",
};

// ─── JSON Tree ────────────────────────────────────────────────────────────────

const JsonNode = ({ data, depth = 0 }) => {
  const [collapsed, setCollapsed] = useState(depth > 1);

  if (data === null || data === undefined)
    return <span className="text-muted-foreground italic">null</span>;
  if (typeof data === "boolean")
    return <span className="text-violet-600">{String(data)}</span>;
  if (typeof data === "number")
    return <span className="text-blue-600">{data}</span>;
  if (typeof data === "string")
    return <span className="text-emerald-700">"{data}"</span>;

  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="text-muted-foreground">[]</span>;
    return (
      <span>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="text-muted-foreground hover:text-foreground font-mono"
        >
          {collapsed ? "▶" : "▼"} [{data.length}]
        </button>
        {!collapsed && (
          <div className="ml-4 border-l border-border pl-2 mt-0.5 space-y-0.5">
            {data.map((item, i) => (
              <div key={i}>
                <span className="text-muted-foreground">{i}: </span>
                <JsonNode data={item} depth={depth + 1} />
              </div>
            ))}
          </div>
        )}
      </span>
    );
  }

  if (typeof data === "object") {
    const keys = Object.keys(data);
    if (keys.length === 0) return <span className="text-muted-foreground">{"{}"}</span>;
    return (
      <span>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="text-muted-foreground hover:text-foreground font-mono"
        >
          {collapsed ? "▶" : "▼"} {"{" + keys.length + "}"}
        </button>
        {!collapsed && (
          <div className="ml-4 border-l border-border pl-2 mt-0.5 space-y-0.5">
            {keys.map((k) => (
              <div key={k}>
                <span className="text-foreground font-medium">{k}: </span>
                <JsonNode data={data[k]} depth={depth + 1} />
              </div>
            ))}
          </div>
        )}
      </span>
    );
  }

  return <span className="text-foreground">{String(data)}</span>;
};

// ─── Subsystem Section ────────────────────────────────────────────────────────

const SubsystemSection = ({ name, subsystem, sectionRef }) => {
  const [expanded, setExpanded] = useState(false);
  const colors = STATUS_COLORS[subsystem.status] || STATUS_COLORS.ok;

  return (
    <div ref={sectionRef} className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-4 py-3 bg-card hover:bg-muted/50 transition-colors text-left gap-3"
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded
            ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
          <span className="text-sm font-semibold text-foreground">
            {SUBSYSTEM_LABELS[name] || name}
          </span>
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium shrink-0 ${colors.pill}`}>
            {STATUS_ICONS[subsystem.status]}
            {subsystem.status}
          </span>
        </div>
      </button>

      <div className="px-4 pb-3">
        <p className="text-xs text-muted-foreground leading-relaxed">{subsystem.summary}</p>
      </div>

      {expanded && subsystem.details && (
        <div className="border-t border-border px-4 py-3 bg-muted/50 font-mono text-xs overflow-auto max-h-72">
          <JsonNode data={subsystem.details} depth={0} />
        </div>
      )}
    </div>
  );
};

// ─── Main Drawer ──────────────────────────────────────────────────────────────

const DebugDrawer = ({ callId, runId, targetLabel, onClose }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const sectionRefs = useRef({});

  useEffect(() => {
    const fetcher = runId
      ? debugAPI.getRunDebug(runId)
      : debugAPI.getCallDebug(callId);
    fetcher
      .then(setData)
      .catch((err) =>
        setFetchError(err.response?.data?.error || "Failed to load debug info")
      )
      .finally(() => setLoading(false));
  }, [callId, runId]);

  const scrollToSection = (key) => {
    sectionRefs.current[key]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const overallColors = data ? STATUS_COLORS[data.overall] || STATUS_COLORS.ok : null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/25 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[480px] bg-card shadow-2xl z-50 flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Bug className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-semibold text-foreground text-sm truncate">
              Debug — {targetLabel}
            </span>
            {overallColors && data && (
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium shrink-0 ${overallColors.pill}`}>
                {STATUS_ICONS[data.overall]}
                {data.overall}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-muted-foreground transition-colors shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center h-40 gap-2 text-muted-foreground">
              <div className="h-5 w-5 border-2 border-border border-t-gray-500 rounded-full animate-spin" />
              <span className="text-sm">Running diagnostics…</span>
            </div>
          )}

          {/* Fetch error */}
          {fetchError && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
              {fetchError}
            </div>
          )}

          {/* Results */}
          {data && !loading && (
            <>
              {/* Quick-nav pills */}
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.subsystems).map(([key, sub]) => {
                  const colors = STATUS_COLORS[sub.status] || STATUS_COLORS.ok;
                  return (
                    <button
                      key={key}
                      onClick={() => scrollToSection(key)}
                      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium hover:opacity-80 transition-opacity ${colors.pill}`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
                      {SUBSYSTEM_LABELS[key] || key}
                    </button>
                  );
                })}
              </div>

              {/* Subsystem sections */}
              <div className="space-y-3">
                {Object.entries(data.subsystems).map(([key, sub]) => (
                  <SubsystemSection
                    key={key}
                    name={key}
                    subsystem={sub}
                    sectionRef={(el) => { sectionRefs.current[key] = el; }}
                  />
                ))}
              </div>

              {/* Error log */}
              {data.error_log && data.error_log.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Error Log ({data.error_log.length})
                  </h4>
                  <div className="space-y-2">
                    {data.error_log.map((entry, i) => (
                      <div
                        key={i}
                        className="bg-red-50 border border-red-100 rounded-lg p-3 text-xs"
                      >
                        <div className="font-semibold text-red-700">
                          {entry.step || "Error"}
                        </div>
                        <div className="text-red-600 mt-0.5 leading-relaxed">
                          {entry.message || entry.error || JSON.stringify(entry)}
                        </div>
                        {entry.occurred_at && (
                          <div className="text-red-400 mt-1 font-mono">
                            {new Date(entry.occurred_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DebugDrawer;
